import { useLocation, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Phone, MessageCircle, ChevronRight, MapPin } from "lucide-react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import Faq from "@/components/sections/Faq";
import ContactSection from "@/components/sections/ContactSection";
import Reveal from "@/components/motion/Reveal";
import { ServiceIcon } from "@/components/icons";
import { getService, SERVICES } from "@/data/services";
import { SERVICE_SEO } from "@/data/serviceSeo";
import { SITE, SERVICE_AREAS } from "@/data/site";
import Konfigurator from "@/components/Konfigurator";
import ExtraSolutions from "@/components/sections/ExtraSolutions";

export default function ServicePage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").replace(/\/$/, "");
  const s = getService(slug);
  if (!s) return <Navigate to="/404" replace />;

  const emergency = s.emergency;
  const accent = emergency ? "#FF3B30" : "#fff000";
  const related = SERVICES.filter((x) => x.slug !== s.slug && x.world === s.world).slice(0, 3);
  const relatedFallback = related.length ? related : SERVICES.filter((x) => x.slug !== s.slug).slice(0, 3);
  const showConfigurator = ["chiptuning-cloppenburg", "softwareoptimierung-cloppenburg"].includes(s.slug);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: s.h1,
    provider: {
      "@type": "AutoRepair",
      name: SITE.name,
      telephone: SITE.phoneIntl,
      address: {
        "@type": "PostalAddress",
        streetAddress: SITE.address.street,
        postalCode: SITE.address.zip,
        addressLocality: SITE.address.city,
        addressCountry: "DE",
      },
    },
    areaServed: "Cloppenburg, Oldenburger Münsterland",
    description: s.metaDescription,
    keywords: s.keywords.join(", "),
  };

  return (
    <Layout>
      <Seo title={s.metaTitle} description={s.metaDescription} path={`/${s.slug}`} keywords={s.keywords} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20">
        <div className="absolute inset-0 z-0 opacity-30">
          <img src={s.image} alt={s.h1} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-[#050505]/50" />
        </div>
        <div className="absolute inset-0 z-0 radial-glow" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
          {/* Breadcrumb */}
          <nav className="mb-8 flex items-center gap-2 font-body text-xs text-neutral-500" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-volt">Start</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-neutral-300">{s.nav}</span>
          </nav>

          <Reveal>
            <div className="mb-6 flex h-16 w-16 items-center justify-center border" style={{ borderColor: `${accent}66`, color: accent }}>
              <ServiceIcon name={s.icon} className="h-8 w-8" />
            </div>
            <p className="mb-3 font-body text-[11px] font-bold uppercase tracking-[0.3em]" style={{ color: accent }}>
              {s.eyebrow}
            </p>
            <h1 className="max-w-4xl font-heading font-black uppercase leading-[0.88] tracking-tight text-white" style={{ fontSize: "clamp(2.5rem, 7vw, 6rem)" }}>
              {s.h1}
            </h1>
            <p className="mt-7 max-w-2xl font-body text-base leading-relaxed text-neutral-300 sm:text-lg">
              {s.intro}
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a href={`tel:${SITE.phoneIntl}`} data-testid="service-call-btn"
                 className="inline-flex items-center justify-center gap-2 px-8 py-4 font-body text-sm font-bold uppercase tracking-wide text-black transition-transform hover:scale-[1.03]"
                 style={{ backgroundColor: accent }}>
                <Phone className="h-5 w-5" /> {SITE.phoneDisplay}
              </a>
              <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="service-whatsapp-btn"
                 className="inline-flex items-center justify-center gap-2 border border-white/20 px-8 py-4 font-body text-sm font-bold uppercase tracking-wide text-white transition-colors hover:border-volt hover:text-volt">
                <MessageCircle className="h-5 w-5" /> WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Konfigurator (Chiptuning & Softwareoptimierung) */}
      {showConfigurator && (
        <section id="konfigurator" className="relative bg-[#050505] py-20 sm:py-24" data-testid="konfigurator-section">
          <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
            <Reveal className="mb-10 max-w-2xl">
              <p className="mb-4 flex items-center gap-3 font-body text-[11px] font-bold uppercase tracking-[0.3em] text-volt">
                <span className="h-px w-10 bg-volt" /> Live-Konfigurator
              </p>
              <h2 className="font-heading text-3xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-5xl">
                Dein Leistungs-Plus berechnen
              </h2>
              <p className="mt-5 font-body text-base text-neutral-400">
                Wähle Marke, Modell und Motor – und sieh direkt, welches Potenzial in
                deinem Fahrzeug steckt. Anschließend erstellen wir dir dein persönliches Angebot.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <Konfigurator />
            </Reveal>
          </div>
        </section>
      )}

      {/* Benefits */}
      <section className="bg-[#080808] py-24 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal className="mb-14 max-w-2xl">
            <p className="mb-4 flex items-center gap-3 font-body text-[11px] font-bold uppercase tracking-[0.3em] text-volt">
              <span className="h-px w-10 bg-volt" /> Deine Vorteile
            </p>
            <h2 className="font-heading text-3xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-5xl">
              Warum KeyMaster
            </h2>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2">
            {s.benefits.map((b, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: (i % 2) * 0.1 }}
                className="flex gap-4 border border-white/10 bg-[#0d0d0d] p-7 transition-colors hover:border-volt/40">
                <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-volt text-black">
                  <Check className="h-4 w-4" strokeWidth={3} />
                </span>
                <div>
                  <h3 className="font-heading text-xl font-bold uppercase text-white">{b.title}</h3>
                  <p className="mt-1.5 font-body text-sm text-neutral-400">{b.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Extra tuning solutions (Chiptuning & Softwareoptimierung) */}
      {showConfigurator && <ExtraSolutions />}

      {/* Process */}
      <section className="bg-[#050505] py-24 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal className="mb-14 max-w-2xl">
            <p className="mb-4 flex items-center gap-3 font-body text-[11px] font-bold uppercase tracking-[0.3em] text-volt">
              <span className="h-px w-10 bg-volt" /> Der Ablauf
            </p>
            <h2 className="font-heading text-3xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-5xl">
              Schritt für Schritt
            </h2>
          </Reveal>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {s.process.map((p, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border border-white/10 bg-[#0d0d0d] p-7">
                <span className="font-heading text-4xl font-black text-volt">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-heading text-xl font-bold uppercase text-white">{p.title}</h3>
                <p className="mt-2 font-body text-sm text-neutral-400">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rich SEO content + coverage area */}
      {SERVICE_SEO[s.slug] && (
        <section className="bg-[#080808] py-20 sm:py-24" data-testid="service-seo-block">
          <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
            <div className="grid gap-12 lg:grid-cols-[1.15fr_0.85fr]">
              <Reveal>
                <h2 className="font-heading text-3xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-4xl">
                  {SERVICE_SEO[s.slug].heading}
                </h2>
                <div className="mt-6 space-y-5">
                  {SERVICE_SEO[s.slug].paragraphs.map((p, i) => (
                    <p key={i} className="font-body text-base leading-relaxed text-neutral-400">{p}</p>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={0.1}>
                <div className="border border-white/10 bg-[#0d0d0d] p-8">
                  <p className="mb-5 font-body text-[11px] font-bold uppercase tracking-[0.2em] text-volt">
                    Leistungen im Detail
                  </p>
                  <ul className="space-y-3">
                    {SERVICE_SEO[s.slug].checklist.map((c, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="mt-0.5 h-5 w-5 shrink-0 text-volt" strokeWidth={3} />
                        <span className="font-body text-sm text-neutral-300">{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>

            {/* Coverage area — strong local SEO signal */}
            <Reveal delay={0.05}>
              <div className="mt-14 border-t border-white/10 pt-10">
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-volt" />
                  <h3 className="font-heading text-xl font-bold uppercase text-white">
                    {s.nav} – unser Einsatzgebiet rund um Cloppenburg
                  </h3>
                </div>
                <div className="mt-5 flex flex-wrap gap-2.5">
                  {SERVICE_AREAS.map((a) => (
                    <span key={a} className="border border-white/10 bg-[#0d0d0d] px-4 py-2 font-body text-xs font-semibold uppercase tracking-wide text-neutral-400 transition-colors hover:border-volt/50 hover:text-white">
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* FAQ */}
      <Faq items={s.faq} title="Fragen zu diesem Service" subtitle={`${s.nav} im Raum ${SITE.address.city} – die wichtigsten Antworten.`} />

      {/* Related */}
      <section className="bg-[#050505] py-20">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <h2 className="mb-8 font-heading text-2xl font-black uppercase text-white sm:text-3xl">Weitere Leistungen</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {relatedFallback.map((r) => (
              <Link key={r.slug} to={`/${r.slug}`} data-testid={`related-${r.slug}`}
                className="group flex items-center justify-between border border-white/10 bg-[#0d0d0d] p-6 transition-all hover:border-volt hover:-translate-y-1">
                <div className="flex items-center gap-4">
                  <ServiceIcon name={r.icon} className="h-6 w-6 text-volt" />
                  <span className="font-heading text-lg font-bold uppercase text-white">{r.nav}</span>
                </div>
                <ArrowRight className="h-5 w-5 text-neutral-600 transition-all group-hover:translate-x-1 group-hover:text-volt" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ContactSection defaultService={s.nav} />
    </Layout>
  );
}
